import React, { useState, useEffect, useCallback } from 'react';
// import { Link } from 'react-router-dom';
import AgentCard from '../components/agent/AgentCard'; 
import Button from '../components/common/Button';
import { useWallet } from '../contexts/WalletContext';
import { getAgentsForUser } from '../services/agentService';
import { PlusCircleIcon, UserGroupIcon, ExclamationTriangleIcon, InformationCircleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Spinner from '../components/common/Spinner';
import CreateAgentModal from '../components/agent/CreateAgentModal'; 

const MyAgentsPage = () => {
  const { currentAccount } = useWallet();
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('latest'); 
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false); 

  const fetchAgents = useCallback(async () => {
    console.log("Fetching agents for current account:", currentAccount);
    if (currentAccount) {
      console.log("Fetching agents for account:", currentAccount);  
      setIsLoading(true);
      setError('');
      try {
        const userAgents = await getAgentsForUser(currentAccount);
        setAgents(userAgents);
      } catch (err) {
        console.error("Failed to fetch user agents:", err);
        setError(err.message || "Could not load your agents.");
      } finally {
        setIsLoading(false);
      }
    } else {
      setAgents([]);
      setError("Please connect your wallet to view your agents.");
    }
  }, [currentAccount]); 

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]); 

  useEffect(() => {
    let result = Array.isArray(agents) ? [...agents] : [];
    
    // Filter by search
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter(agent => 
        (agent.name || '').toLowerCase().includes(lowerTerm) ||
        (agent.description || '').toLowerCase().includes(lowerTerm)
      );
    }

    // Sort
    if (filter === 'latest') {
        // Assuming higher ID or just keeping order for now if no timestamps
        result.reverse(); 
    }
    
    setFilteredAgents(result);
  }, [agents, searchTerm, filter]);


  const handleAgentCreated = () => {
    fetchAgents();
    // setIsCreateModalOpen(false); 
  };

  return (
    <div className="container mx-auto px-4 py-8">
      
      {/* Header Section */}
      <div className="mb-10 text-center">
         <h1 className="text-3xl md:text-5xl font-bold text-white mb-2">AI Agent Market</h1>
         <p className="text-slate-400">Discover and manage your autonomous AI agents</p>
      </div>

      {/* Control Bar */}
      <div className="flex flex-col lg:flex-row justify-between items-center gap-4 mb-8">
          {/* Search */}
          <div className="relative w-full lg:max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-3 rounded-xl border border-slate-700 bg-slate-800/50 text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all shadow-sm"
              placeholder="Search for AI Agents..."
            />
          </div>

          {/* Filters & Actions */}
          <div className="flex items-center gap-3 w-full lg:w-auto">
             <div className="bg-slate-800 rounded-lg p-1 border border-slate-700 flex">
                <button 
                  onClick={() => setFilter('latest')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === 'latest' ? 'bg-slate-700 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                >
                  Latest
                </button>
                <button 
                  onClick={() => setFilter('oldest')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === 'oldest' ? 'bg-slate-700 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                >
                  Oldest
                </button>
             </div>

             {currentAccount && (
              <Button
                variant="primary" 
                onClick={() => setIsCreateModalOpen(true)}
                className="!rounded-xl !px-6 !py-2.5 bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-500 hover:to-indigo-500 border-none shadow-lg shadow-indigo-900/20 whitespace-nowrap"
              >
                <PlusCircleIcon className="h-5 w-5 mr-2 inline-block" />
                Create New Agent
              </Button>
            )}
          </div>
      </div>


      {isLoading && (
        <div className="flex justify-center py-20">
          <Spinner size="lg" color="blue" /> 
        </div>
      )}

      {!isLoading && error && (
        <div className={`text-center py-10 rounded-xl p-6 shadow-lg border mb-8
                         ${!currentAccount ? 'bg-yellow-900/30 backdrop-blur-sm border-yellow-700/50 text-yellow-300' 
                                           : 'bg-red-900/30 backdrop-blur-sm border-red-700/50 text-red-300'}`}>
          {!currentAccount ? (
            <InformationCircleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          ) : (
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          )}
          <p className="text-xl font-semibold">{error}</p>
          {!currentAccount && <p className="mt-2 text-sm">Make sure your wallet is connected to view your agents.</p>}
        </div>
      )}

      {!isLoading && !error && filteredAgents.length === 0 && currentAccount && (
         <div className="text-center py-20 bg-slate-800/30 backdrop-blur-md border border-slate-700/50 rounded-2xl shadow-lg border-dashed">
            <UserGroupIcon className="h-16 w-16 mx-auto text-slate-500 mb-4 opacity-50" />
            <p className="text-xl text-slate-300 mb-2">No Agents Found</p>
            <p className="text-sm text-slate-400 mb-8 max-w-md mx-auto">
              {searchTerm ? "No agents match your search criteria." : "You haven't created any AI agents yet. Start building your autonomous workforce."}
            </p>
            {!searchTerm && (
                <Button 
                variant="primary" 
                onClick={() => setIsCreateModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-500 border-none shadow-lg shadow-blue-900/20"
                >
                    Create Your First Agent
                </Button>
            )}
        </div>
       )}

      {!isLoading && !error && filteredAgents.length > 0 && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(340px,1fr))] gap-6 pb-12">
          {filteredAgents.map(agent => (
            <AgentCard key={agent.agent_id} agent={agent} />
          ))}
        </div>
      )}

    
      <CreateAgentModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onAgentCreated={handleAgentCreated}
      />
    </div>
  );
};

export default MyAgentsPage;